import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LeagueDetailsEditor from './LeagueDetailsEditor';
import ManageSideEvent from './ManageSideEvent';
import AdminSideActionDashboard from './AdminSideActionDashboard';
import ManageTeams from './ManageTeams';
import ManageSchedule from './ManageSchedule';
import MatchScoring from './MatchScoring';
import CheckInComponent from './CheckInComponent';
import FinancialComponent from './FinancialComponent';
import Leaderboard from './Leaderboard';
import InviteBowlersModal from './InviteBowlersModal';
import AdminHandicapConfigModal from './AdminHandicapConfigModal';

const BACKEND_URL = process.env.REACT_APP_API_URL;

function YoutubeLite({ id, title }: { id: string; title: string }) {
  const [active, setActive] = useState(false);
  return (
    <div className="bg-white/30 rounded-2xl shadow-lg border border-white/30 overflow-hidden">
      <div className="px-4 py-3 border-b border-white/20">
        <p className="text-sm text-white">{title}</p>
      </div>
      <div className="relative w-full cursor-pointer" style={{ paddingBottom: '56.25%' }} onClick={() => setActive(true)}>
        {active ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${id}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            <img
              className="absolute inset-0 w-full h-full object-cover"
              src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
              alt={title}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-red-600 hover:bg-red-500 transition-colors rounded-xl px-5 py-3 flex items-center gap-2 shadow-lg">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <span className="text-white text-sm font-semibold">Play</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  admin_id: number;
  isActive: boolean;
  hidden?: number;
}

interface HandicapConfig {
  base_average: number;
  handicap_percentage: number;
  calculation_frequency: string;
  lock_handicap_after_weeks: number | null;
  lock_starting_hdcp_for_weeks?: number | null;
  a_max: number | null;
  b_max: number | null;
  c_max: number | null;
  d_max: number | null;
  e_max: number | null;
  league_max: number | null;
  handicap_type: 'individual' | 'team_difference';
  team_difference_percentage: number | null;
}

interface SideEvent {
  id: number;
  name: string;
  description: string;
  type: string;
  applies_to: string;
  cost: number;
  entryCount: number;
  bracket_size?: number;
  league_name: string;
  squad_name?: string;
  squad_date?: string;
}

const ManagingLeague: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();


  const [staffRoles, setStaffRoles] = useState<number[]>([]);

  useEffect(() => {
    const fetchStaffRoles = async () => {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/leagues/${id}/staff-roles`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStaffRoles(Array.isArray(data) ? data.map(Number) : []);
      }
    };
    if (user && user.type === 'admin') {
      fetchStaffRoles();
    }
  }, [id, user]);

  const [league, setLeague] = useState<League | null>(null);
  const [sideEvents, setSideEvents] = useState<SideEvent[]>([]);
  const [selectedSideEvent, setSelectedSideEvent] = useState<SideEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<string>('overview');
  const [showLeagueEditor, setShowLeagueEditor] = useState(false);
  const [registeredTeamsCount, setRegisteredTeamsCount] = useState(0);
  const [scoringMatchId, setScoringMatchId] = useState<number | null>(null);
  const [handicapConfig, setHandicapConfig] = useState<HandicapConfig | null>(null);
  const [showHandicapConfigModal, setShowHandicapConfigModal] = useState(false);
  const [showInviteBowlersModal, setShowInviteBowlersModal] = useState(false);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);

  const handleToggleVisibility = async () => {
    if (!league) return;
    const newHidden = isHidden ? 0 : 1;
    setVisibilityLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/leagues/${league.id}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ hidden: newHidden })
      });
      if (res.ok) {
        setIsHidden(newHidden === 1);
      }
    } catch {
      // ignore
    } finally {
      setVisibilityLoading(false);
    }
  };

  const fetchLeagueData = React.useCallback(async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';

      // Fetch league details
      const leagueResponse = await fetch(`${apiUrl}/leagues/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (leagueResponse.ok) {
        const leagueData = await leagueResponse.json();
        setLeague(leagueData);
        setIsHidden(leagueData.hidden === 1);
      }

      // Fetch management data (side events with entry counts, teams count)
      const managementResponse = await fetch(`${apiUrl}/admin/leagues/${id}/management`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (managementResponse.ok) {
        const managementData = await managementResponse.json();
        setSideEvents(managementData.sideEvents);
        setRegisteredTeamsCount(managementData.registeredTeamsCount);
      }

      // Fetch handicap configuration
      const handicapResponse = await fetch(`${apiUrl}/admin/leagues/${id}/handicap-config`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (handicapResponse.ok) {
        const handicapData = await handicapResponse.json();
        setHandicapConfig(handicapData);
      }

    } catch (error) {
      console.error('Failed to fetch league data:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchLeagueData();
    }
  }, [id, fetchLeagueData]);


  if (!user || user.type !== 'admin') {
    navigate('/admin/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">{t('admin.managingLeague.loading')}</p>
        </div>
      </div>
    );
  }

  if (!league) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('admin.managingLeague.notFound')}</h2>
          <button
            onClick={() => navigate('/admin/leagues/manage')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('admin.managingLeague.backToLeagues')}
          </button>
        </div>
      </div>
    );
  }

  // Render specific component views
  if (activeView === 'side-action-dashboard') {
    return (
      <AdminSideActionDashboard
        leagueId={parseInt(id!)}
        onBack={() => setActiveView('overview')}
        onNavigateToSchedule={() => setActiveView('manage-schedule')}
      />
    );
  }
  if (activeView === 'manage-side-event' && selectedSideEvent) {
    return (
      <ManageSideEvent
        leagueId={parseInt(id!)}
        sideEvent={selectedSideEvent}
        onBack={() => {
          setActiveView('overview');
          setSelectedSideEvent(null);
        }}
      />
    );
  }
  if (activeView === 'manage-teams') {
    return <ManageTeams
      leagueId={parseInt(id!)}
      onBack={() => setActiveView('overview')}
      onNavigateToSchedule={() => setActiveView('manage-schedule')}
      onNavigateToCheckIn={() => setActiveView('check-in')}
    />;
  }
  if (activeView === 'manage-schedule') {
    return (
      <ManageSchedule
        leagueId={parseInt(id!)}
        onBack={() => setActiveView('overview')}
        onNavigateToScoring={(matchId) => {
          setScoringMatchId(matchId);
          setActiveView('match-scoring');
        }}
      />
    );
  }
  if (activeView === 'match-scoring' && scoringMatchId) {
    return (
      <MatchScoring
        matchId={scoringMatchId}
        leagueId={parseInt(id!)}
        leagueName={league.name}
        onBack={() => {
          setScoringMatchId(null);
          setActiveView('manage-schedule');
        }}
      />
    );
  }
  if (activeView === 'check-in') {
    return <CheckInComponent leagueId={parseInt(id!)} onBack={() => setActiveView('overview')} />;
  }
  if (activeView === 'financial') {
    return <FinancialComponent leagueId={parseInt(id!)} staffRoles={staffRoles} onBack={() => setActiveView('overview')} />;
  }
  if (activeView === 'leaderboard') {
    return <Leaderboard
      leagueId={parseInt(id!)}
      leagueName={league?.name}
      staffRoles={staffRoles}
      onBack={() => setActiveView('overview')}
      onNavigateToFinancial={() => setActiveView('financial')}
    />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/leagues/manage')}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-white">{t('admin.managingLeague.managing')} {league.name}</h1>
                <p className="text-blue-200 mt-1">{league.venue}, {league.city}</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${league.isActive
                ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}>
                {league.isActive ? t('admin.managingLeague.active') : t('admin.managingLeague.inactive')}
              </span>
              <button
                onClick={handleToggleVisibility}
                disabled={visibilityLoading}
                title={isHidden ? 'Click to make league visible to bowlers' : 'Click to hide league from bowlers'}
                className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition duration-200 border ${
                  isHidden
                    ? 'bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30'
                    : 'bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30'
                } ${visibilityLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {visibilityLoading ? (
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                ) : isHidden ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
                {isHidden ? 'Hidden' : 'Visible'}
              </button>
            </div>
          </div>
          {(user.id === league.admin_id && (
            <button
              onClick={() => navigate(`/admin/leagues/${league.id}/manage-staff`, {
              })}
              className="px-4 py-1 mb-2 ml-2 rounded-lg bg-yellow-600/50 hover:bg-yellow-700/50 text-white font-medium border border-yellow-400/60 transition duration-200"
            >
              {t('admin.managingLeague.manageStaff')}
            </button>
          ))}
        </div>
      </div>
      {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueHelpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}



      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* League Details Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-white">{t('admin.managingLeague.leagueDetails')}</h2>
            {(user.id === league.admin_id || staffRoles.includes(1)) && (
              <button
                onClick={() => setShowLeagueEditor(true)}
                className="flex items-center space-x-2 bg-blue-600/50 hover:bg-blue-700/50 border border-blue-400/30 text-white px-4 py-2 rounded-lg transition duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>{t('admin.managingLeague.editDetails')}</span>
              </button>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-blue-200">
            <div>
              <p><strong className="text-white">{t('admin.managingLeague.format')}</strong> {league.teamFormat}</p>
              <p><strong className="text-white">{t('admin.managingLeague.startDate')}</strong> {new Date(league.startDate).toLocaleDateString()}</p>
              <p><strong className="text-white">{t('admin.managingLeague.endDate')}</strong> {new Date(league.endDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p><strong className="text-white">{t('admin.managingLeague.venue')}</strong> {league.venue}</p>
              <p><strong className="text-white">{t('admin.managingLeague.location')}</strong> {league.city}, {league.country}</p>
              <p><strong className="text-white">{t('admin.managingLeague.status')}</strong> {league.isActive ? t('admin.managingLeague.active') : t('admin.managingLeague.inactive')}</p>
            </div>
          </div>

          {/* Handicap Configuration Display */}
          {handicapConfig && (
            <div className="mt-6 pt-6 border-t border-white/20">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center justify-between">
                <span className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  {t('admin.managingLeague.handicapConfiguration')}
                </span>
                <button
                  onClick={() => setShowHandicapConfigModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  {t('common.edit')}
                </button>
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                {handicapConfig.handicap_type && (
                  <div className="bg-indigo-500/10 rounded-lg p-3 border border-indigo-400/20">
                    <p className="text-indigo-300 text-xs mb-1">{t('admin.managingLeague.handicapType')}</p>
                    <p className="text-white font-semibold capitalize">
                      {handicapConfig.handicap_type === 'team_difference'
                        ? t('admin.managingLeague.handicapTypeTeamDiff')
                        : t('admin.managingLeague.handicapTypeIndividual')}
                    </p>
                  </div>
                )}
                {handicapConfig.handicap_type === 'team_difference' && handicapConfig.team_difference_percentage != null && (
                  <div className="bg-teal-500/10 rounded-lg p-3 border border-teal-400/20">
                    <p className="text-teal-300 text-xs mb-1">{t('admin.managingLeague.teamDiffPercentage')}</p>
                    <p className="text-white font-semibold">{handicapConfig.team_difference_percentage}%</p>
                  </div>
                )}
                {handicapConfig.handicap_type !== 'team_difference' && (<>
                <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-400/20">
                  <p className="text-blue-300 text-xs mb-1">{t('admin.managingLeague.baseAverage')}</p>
                  <p className="text-white font-semibold text-lg">{handicapConfig.base_average}</p>
                </div>
                <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-400/20">
                  <p className="text-purple-300 text-xs mb-1">{t('admin.managingLeague.handicapPercent')}</p>
                  <p className="text-white font-semibold text-lg">{handicapConfig.handicap_percentage}%</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3 border border-green-400/20">
                  <p className="text-green-300 text-xs mb-1">{t('admin.managingLeague.frequency')}</p>
                  <p className="text-white font-semibold capitalize">{handicapConfig.calculation_frequency.replace('_', ' ')}</p>
                </div>
                {handicapConfig.lock_handicap_after_weeks && (
                  <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-400/20">
                    <p className="text-amber-300 text-xs mb-1">{t('admin.managingLeague.lockAfter')}</p>
                    <p className="text-white font-semibold">{handicapConfig.lock_handicap_after_weeks} {t('admin.managingLeague.weeks')}</p>
                  </div>
                )}
                {(handicapConfig.a_max || handicapConfig.b_max || handicapConfig.c_max ||
                  handicapConfig.d_max || handicapConfig.e_max || handicapConfig.league_max) && (
                    <div className="bg-red-500/10 rounded-lg p-3 border border-red-400/20 sm:col-span-2">
                      <p className="text-red-300 text-xs mb-2">{t('admin.managingLeague.maximumHandicaps')}</p>
                      <div className="grid grid-cols-3 gap-2 text-white">
                        {handicapConfig.a_max && <span className="text-xs">A: {handicapConfig.a_max}</span>}
                        {handicapConfig.b_max && <span className="text-xs">B: {handicapConfig.b_max}</span>}
                        {handicapConfig.c_max && <span className="text-xs">C: {handicapConfig.c_max}</span>}
                        {handicapConfig.d_max && <span className="text-xs">D: {handicapConfig.d_max}</span>}
                        {handicapConfig.e_max && <span className="text-xs">E: {handicapConfig.e_max}</span>}
                        {handicapConfig.league_max && <span className="text-xs">League: {handicapConfig.league_max}</span>}
                      </div>
                    </div>
                  )}
                </>)}
              </div>
            </div>
          )}

        </div>


        {/* Side Events Section */}
        {(user.id === league.admin_id || staffRoles.includes(2)) && (
          <div className="mb-8">
            <div
              onClick={() => setActiveView('side-action-dashboard')}
              className="group relative bg-white/10 backdrop-blur-lg border border-white/30 rounded-xl p-6 hover:bg-purple-600/20 cursor-pointer transition duration-200 overflow-hidden"
            >
              {/* Background screenshot image */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/50 to-transparent z-10" />
                <img
                  src="/sideactionss.png"
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-40 transition duration-300"
                />
              </div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-purple-200 mb-2">{t('admin.managingLeague.manageSideEvents')}</h3>
                  <p className="text-purple-300 text-sm">
                    {sideEvents.length > 0
                      ? `${sideEvents.length} side event type${sideEvents.length !== 1 ? 's' : ''} configured — manage entries, brackets, and scores`
                      : 'Add and manage side action events for each squad'}
                  </p>
                </div>
                <div className="text-5xl">🪙</div>
              </div>
            </div>
          </div>
        )}
        {/* Management Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-6">
          {/* Registered Teams */}
          {(user.id === league.admin_id || staffRoles.includes(5)) && (
            <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-200 relative">
              {/* Invite Bowlers Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInviteBowlersModal(true);
                }}
                className="absolute top-4 right-4 flex items-center space-x-1.5 bg-purple-600/50 hover:bg-purple-700/50 border border-purple-400/30 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium transition duration-200 z-10"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span>{t('admin.inviteBowlers.buttonText')}</span>
              </button>

              <div
                onClick={() => setActiveView('manage-teams')}
                className="cursor-pointer"
              >
                <h3 className="text-xl font-bold text-white mb-2 pr-32">{t('admin.managingLeague.teams')}</h3>
                <p className="text-blue-300 text-sm mb-4">{t('admin.managingLeague.clickToManageTeams')}</p>
                <div className="text-center">
                  <div className="text-4xl font-bold text-white mb-2">{registeredTeamsCount}</div>
                  <p className="text-blue-200 text-sm">{t('admin.managingLeague.teamsRegistered')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Schedule */}
          {(user.id === league.admin_id || staffRoles.includes(4)) && (
            <div
              onClick={() => setActiveView('manage-schedule')}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 cursor-pointer transition duration-200"
            >
              <h3 className="text-xl font-bold text-white mb-2">{t('admin.managingLeague.scheduleScores')}</h3>
              <p className="text-blue-300 text-sm mb-4">{t('admin.managingLeague.manageScheduleDesc')}</p>
              <div className="text-center">
                <div className="text-4xl mb-2">📅</div>
                <p className="text-blue-200 text-sm">{t('admin.managingLeague.viewEdit')}</p>
              </div>
            </div>
          )}

          {/* Check In */}
          {(user.id === league.admin_id || staffRoles.includes(6)) && (
            <div
              onClick={() => setActiveView('check-in')}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 cursor-pointer transition duration-200"
            >
              <h3 className="text-xl font-bold text-green-200 mb-2">{t('admin.managingLeague.checkIn')}</h3>
              <p className="text-green-300 text-sm mb-4">{t('admin.managingLeague.checkInBowlers')}</p>
              <div className="text-center">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-green-200 text-sm font-medium">{t('admin.managingLeague.goToCheckIn')}</p>
              </div>
            </div>
          )}

          {/* Financial */}
          {(user.id === league.admin_id || staffRoles.includes(7)) && (
            <div
              onClick={() => setActiveView('financial')}
              className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 cursor-pointer transition duration-200"
            >
              <h3 className="text-xl font-bold text-white mb-2">{t('admin.managingLeague.financial')}</h3>
              <p className="text-blue-300 text-sm mb-4">{t('admin.managingLeague.managePayouts')}</p>
              <div className="text-center">
                <div className="text-4xl mb-2">💰</div>
                <p className="text-blue-200 text-sm">{t('admin.managingLeague.goToFinancial')}</p>
              </div>
            </div>
          )}
        </div>

        {/* Leaderboard Section */}
        <div className="mt-8">
          <div
            onClick={() => setActiveView('leaderboard')}
            className="bg-white/10 backdrop-blur-lg border border-white/30 rounded-xl p-6 hover:bg-yellow-600/30  cursor-pointer transition duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-yellow-200 mb-2">{t('admin.managingLeague.leaderboard')}</h3>
                <p className="text-yellow-300 text-sm">{t('admin.managingLeague.leaderboardDesc')}</p>
              </div>
              <div className="text-5xl">🏆</div>
            </div>
          </div>
        </div>

        {/* youtube tutorials */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
                <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                </svg>
              </div>
              <h2 className="text-sm font-bold text-white uppercase tracking-widest">League Administrator Tutorials</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { id: 'n9Fj6TxBmFM', title: 'How to register teams to your league!' },
                { id: '6LctUt3C1mM', title: 'Create your league\'s schedule, assign lanes, import scores!' },
                { id: '4crPoJrCtM0', title: 'How to check in your bowlers' },
              ].map(({ id, title }) => (
                <YoutubeLite key={id} id={id} title={title} />
              ))}
            </div>
          </div>

      </div>

      {/* Modals */}
      {showLeagueEditor && (
        <LeagueDetailsEditor
          league={league}
          onClose={() => setShowLeagueEditor(false)}
          onSave={(updatedLeague: League) => {
            setLeague(updatedLeague);
            setShowLeagueEditor(false);
            fetchLeagueData(); // Refresh data
          }}
        />
      )}


      {showHandicapConfigModal && handicapConfig && (
        <AdminHandicapConfigModal
          leagueId={parseInt(id || '0')}
          initialConfig={handicapConfig}
          onSaved={(updated) => setHandicapConfig(updated)}
          onClose={() => setShowHandicapConfigModal(false)}
        />
      )}

      {showInviteBowlersModal && league && (
        <InviteBowlersModal
          leagueId={league.id}
          leagueName={league.name}
          teamFormat={league.teamFormat}
          onClose={() => {
            setShowInviteBowlersModal(false);
            fetchLeagueData(); // Refresh league data to update team count
          }}
        />
      )}
    </div>
  );
};

export default ManagingLeague;